/**
 * O QUE O RÓTULO DA FASE DIZ, E POR QUE ELE DEPENDE DO MODO.
 *
 * ⛔ O DEFEITO QUE ABRIU ISTO (29/09/2026, PE 14/2026 do IPHAN-BA, UASG 343007).
 * O código de fase `LF` estava mapeado fixo para "Fase fechada (envelope
 * sigiloso)". Isso vale em Aberto-Fechado, onde depois da etapa aberta os
 * melhores colocados mandam lance selado. Em modo ABERTO não existe envelope
 * nenhum: ali `LF` quer dizer que a etapa de lances ACABOU.
 *
 * O card então anunciava uma etapa por vir numa disputa que tinha simplesmente
 * terminado, e foi lido como tal. Rótulo errado não dá erro: ele é confiante.
 *
 * ⚠️ NÃO DÁ PARA USAR `isModoAbertoFechado` AQUI. Aquele helper aceita
 * `fase === 'LF'` como prova de que o modo é Aberto-Fechado (é a "Fonte 2"
 * dele) — perguntar a ele durante a fase LF devolveria sempre "sim", e o
 * rótulo nunca mudaria. Aqui se lê o modo DECLARADO, e só ele.
 */

export type ModoDeDisputa =
  | 'aberto'
  | 'aberto_fechado'
  | 'fechado_aberto'
  | 'fechado'
  | 'dispensa'
  | 'desconhecido';

const normalizar = (v: unknown): string =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

/**
 * O modo DECLARADO da disputa, lido só dos metadados.
 *
 * Os valores em produção (medidos em 29/09/2026) são heterogêneos: convivem
 * "Aberto" (278), "Aberto-Fechado" (124), "ABERTO" (40), "ABERTO E FECHADO"
 * (9), "Aberto/Fechado", "Fechado-Aberto", "Dispensa Com Disputa", "Não se
 * aplica" e 114 em branco. Mais os códigos do enum do portal
 * (A, F, AF, FA, D) e os legados 1 e 2.
 *
 * ⚠️ O COMPOSTO É TESTADO ANTES DO SIMPLES. "Aberto-Fechado" contém "Aberto":
 * um `/aberto/` solto classificaria como aberto a maior parte das disputas que
 * TÊM envelope sigiloso — exatamente o erro espelhado do que se está
 * consertando.
 */
export function modoDeclarado(item: any, participacao?: any): ModoDeDisputa {
  const bruto =
    participacao?.modoDisputa ??
    item?.modoDisputa ??
    item?.tipoDisputa ??
    item?.modoDisputaNome;
  const t = normalizar(bruto);
  if (!t) return 'desconhecido';

  // Códigos exatos do enum, e os legados numéricos.
  if (t === 'af' || t === '2') return 'aberto_fechado';
  if (t === 'fa') return 'fechado_aberto';
  if (t === 'a' || t === '1') return 'aberto';
  if (t === 'f') return 'fechado';
  if (t === 'd') return 'dispensa';

  // Nomes por extenso. Compostos primeiro.
  if (/fechado[\s\-/e]+aberto/.test(t)) return 'fechado_aberto';
  if (/aberto[\s\-/e]+fechado/.test(t)) return 'aberto_fechado';
  if (/dispensa/.test(t)) return 'dispensa';
  if (/aberto/.test(t)) return 'aberto';
  if (/fechado/.test(t)) return 'fechado';
  return 'desconhecido';
}

/** Tem etapa de lance selado depois da aberta? Só aí "envelope" faz sentido. */
export const temEnvelopeSigiloso = (modo: ModoDeDisputa): boolean =>
  modo === 'aberto_fechado' || modo === 'fechado_aberto';

const BASE: Record<string, string> = {
  LA: 'Fase aberta',
  AL: 'Fase aleatória',
  AF: 'Aguardando fase fechada',
  FE: 'Aguardando encerramento',
};

/**
 * O rótulo do código de fase, para o modo em questão.
 *
 * Com o modo DESCONHECIDO — 114 itens em produção estão com o campo em branco —
 * o `LF` sai como "Fase fechada", sem parênteses: não inventa o envelope e não
 * afirma o encerramento. Mesma disciplina do preço da Meta: quando não se sabe
 * o mecanismo, não se afirma o mecanismo.
 */
export function rotuloDaFase(fase: string, modo: ModoDeDisputa): string | null {
  if (!fase) return null;
  if (fase === 'LF') {
    if (temEnvelopeSigiloso(modo)) return 'Fase fechada (envelope sigiloso)';
    if (modo === 'desconhecido') return 'Fase fechada';
    return 'Etapa de lances encerrada';
  }
  return BASE[fase] ?? null;
}

/**
 * A ETAPA DE LANCES AINDA ESTÁ CORRENDO? Fonte única para o selo, o stepper, a
 * caixa de tempo e a ação oferecida no card.
 *
 * ⛔ O DEFEITO QUE TROUXE ISTO PARA CÁ (01/10/2026, PE 4/2026 da PF, UASG
 * 200342, sessão 150). Com `fase = FE` e o fim de contagem às 10:42:50, às
 * 10:50 o card dizia "Aguardando encerramento" no selo e, ao mesmo tempo, "Em
 * disputa" no stepper, mantinha a caixa de TEMPO RESTANTE e oferecia "Parar".
 * Eram três regras sobre a mesma fase, cada componente com a sua, e só a do
 * selo estava certa. Terceira reclamação do mesmo card.
 *
 * ⚠️ NÃO DERIVE ISTO DO STATUS DA SESSÃO. Enquanto a coleta é inconclusiva o
 * backend mantém a sessão `ativo` de propósito: finalizar no escuro já mandou
 * sessão para "Seleção de fornecedores" com o pregão aberto. `ativo` quer dizer
 * "o robô ainda está olhando", não "ainda dá para lançar".
 *
 * ⚠️ A FASE VEM DA LINHA VIVA (`rankingData`), NÃO DO `itemLimits`. Na sessão
 * 150 o `itemLimits` ainda dizia `LA` enquanto o portal já dizia `FE`.
 *
 * Regra:
 *  - contagem ainda no futuro em qualquer item → etapa correndo, seja qual for o
 *    código. Errar para "correndo" custa um botão a mais; errar para
 *    "encerrada" esconde uma disputa viva.
 *  - `FE` (aguardando encerramento) e `E` (encerrada) → lances encerrados.
 *  - `LF` só conta quando o rótulo já diz "Etapa de lances encerrada", isto é,
 *    modo declarado sem envelope. Em Aberto-Fechado `LF` é o envelope e ainda
 *    se lança; com o modo desconhecido não se afirma nada.
 */
export type SituacaoDaEtapa = {
  /** Código de fase do portal, lido da linha viva. */
  fase: string | null;
  modo: ModoDeDisputa;
  /** O mesmo texto do selo. */
  rotulo: string | null;
  lancesEncerrados: boolean;
  /** Fim de contagem do último reset conhecido, em ms; sem ele, o carimbo. Só com lances encerrados. */
  encerradaEm: number | null;
  /** `lancesEncerradosAt` da sessão, em ms: o instante em que o MOTOR viu o fim dos lances. */
  carimbo: number | null;
};

const linhasVivas = (session: any): any[] => {
  const r = session?.rankingData;
  const raw = r?.itensGrupo?.['-1'] || r?.emDisputa;
  if (Array.isArray(raw)) return raw;
  return raw?.registros || raw?.itens || raw?.items || [];
};

/**
 * Os fins de contagem conhecidos, em ms. O portal manda `dataHoraFimContagem`
 * sem fuso e em hora de Brasília; `new Date` lê como hora local, a mesma
 * leitura que o countdown do card sempre fez.
 */
const finsDeContagem = (session: any, linhas: any[]): number[] => {
  const ts: number[] = [];
  for (const l of linhas) {
    if (l?.dataHoraFimContagem) ts.push(new Date(l.dataHoraFimContagem).getTime());
  }
  for (const lim of (session?.itemLimits || []) as any[]) {
    if (lim?.fimContagem) ts.push(new Date(lim.fimContagem).getTime());
  }
  return ts.filter((t) => isFinite(t));
};

export function situacaoDaEtapa(session: any, agora: number = Date.now()): SituacaoDaEtapa {
  const linhas = linhasVivas(session);
  const fase: string | null = linhas[0]?.fase ?? null;
  const modo = modoDeclarado(session?.item, session?.rankingData?.participacao);
  const rotulo = fase ? rotuloDaFase(fase, modo) : null;
  const fins = finsDeContagem(session, linhas);
  const contagemCorrendo = fins.some((t) => t > agora);

  const encerradaPelaFase =
    fase === 'FE' || fase === 'E' || (fase === 'LF' && rotulo === 'Etapa de lances encerrada');
  // ⭐ O CARIMBO DO MOTOR (backend #505, 20/10/2026). `lancesEncerradosAt` é gravado uma vez, por quem
  // olhou o portal, e o status segue `ativo` de propósito (o cron de coleta filtra por ele). Ele vale
  // sem a fase: sobrevive ao deploy, quando o `rankingData` ainda não foi relido. ⚠️ Não vence uma
  // contagem viva no futuro, pela mesma regra de cima: errar para "correndo" custa um botão a mais.
  const t = session?.lancesEncerradosAt ? new Date(session.lancesEncerradosAt).getTime() : NaN;
  const carimbo = isFinite(t) ? t : null;
  const lancesEncerrados = !contagemCorrendo && (encerradaPelaFase || carimbo != null);

  return {
    fase,
    modo,
    rotulo,
    lancesEncerrados,
    encerradaEm: !lancesEncerrados ? null : fins.length > 0 ? Math.max(...fins) : carimbo,
    carimbo,
  };
}
